import React, { useCallback, useState } from 'react';

import Badge from '@liquidweb-dev/undercurrent/Badge';
import Box from '@liquidweb-dev/undercurrent/Box';
import Divider from '@liquidweb-dev/undercurrent/Divider';
import IconButton from '@liquidweb-dev/undercurrent/IconButton';
import EyeIcon from '@liquidweb-dev/undercurrent/icons/animated/Eye';
import ArrowUpRightIcon from '@liquidweb-dev/undercurrent/icons/lucide/ArrowUpRight';
import CircleHelpIcon from '@liquidweb-dev/undercurrent/icons/lucide/CircleHelp';
import PencilIcon from '@liquidweb-dev/undercurrent/icons/lucide/Pencil';
import Link from '@liquidweb-dev/undercurrent/Link';
import ListItemIcon from '@liquidweb-dev/undercurrent/ListItemIcon';
import ListItemText from '@liquidweb-dev/undercurrent/ListItemText';
import Menu, { menuClasses } from '@liquidweb-dev/undercurrent/Menu';
import MenuItem from '@liquidweb-dev/undercurrent/MenuItem';
import Skeleton from '@liquidweb-dev/undercurrent/Skeleton';
import Stack from '@liquidweb-dev/undercurrent/Stack';
import Typography from '@liquidweb-dev/undercurrent/Typography';

import { IntroTarget, introTargets } from 'containers/introjs';
import useAuthenticated from 'modules/oidc/useAuthenticated';
import useAccountChatPasswordGet from 'modules/queries/account/chatPassword/useGet';
import useSecureDataShareList from 'modules/queries/secureDataShare/useList';
import useLeftHandLayout from '../context';
import { useNavDialogsContext } from '../navDialogsContext';
import HookedLink from './HookedLink';

export function TopNavSupport() {
	const [menuElement, setMenuElement] = useState();
	const { setIsSecureNotesOpen, setIsEditUserOpen } = useNavDialogsContext();

	const open = menuElement !== undefined;

	const { roles } = useLeftHandLayout();

	const [showSupportPassphrase, setShowSupportPassphrase] = useState(false);
	const {
		data: supportPassphraseData,
		isLoading: isLoadingSupportPassphraseRaw,
	} = useAccountChatPasswordGet({ enabled: showSupportPassphrase });

	const isAuthed = useAuthenticated();
	const { data: supportCasesData } = useSecureDataShareList(
		{ page_size: 1 },
		{ enabled: isAuthed, refetchInterval: 30000 },
	);

	const hasSupportCases = (supportCasesData?.items.length || 0) > 0;

	const isLoadingSupportPassphrase =
		isLoadingSupportPassphraseRaw && showSupportPassphrase;

	const supportPassphrase = showSupportPassphrase
		? supportPassphraseData?.password
		: '●●●●●●●●●●●●●●●●●●●●●●●●●●●';

	const handleClose = useCallback(() => {
		setShowSupportPassphrase(false);
		setMenuElement();
	}, []);

	if (!roles.loggedIn) {
		return null;
	}

	return (
		<>
			<IntroTarget introId={introTargets.navHelpMenu}>
				<Badge
					color={hasSupportCases ? 'error' : undefined}
					variant="dot"
					slotProps={{ badge: { sx: { top: '2px', right: '2px' } } }}
				>
					<IconButton
						variant="secondary"
						size="large"
						onClick={({ currentTarget }) => setMenuElement(currentTarget)}
					>
						<Box sx={{ display: 'flex', alignItems: 'center', gap: 100 }}>
							<CircleHelpIcon />
							<Typography variant="label-lg" sx={{ fontWeight: 500 }}>
								Support
							</Typography>
						</Box>
					</IconButton>
				</Badge>
			</IntroTarget>
			<Menu
				open={open}
				anchorEl={menuElement}
				anchorOrigin={{
					vertical: 'bottom',
					horizontal: 'right',
				}}
				transformOrigin={{
					vertical: 'top',
					horizontal: 'right',
				}}
				onClose={handleClose}
				sx={{
					zIndex: { xs: 1201, sm: 1200 },
					[`.${menuClasses.list}`]: { width: '248px' },
				}}
			>
				<MenuItem
					component={HookedLink}
					href="/support/mycases"
					onClick={handleClose}
					role="menuitem"
				>
					<ListItemText primary="My support cases" />
				</MenuItem>
				<MenuItem
					component={Link}
					href="https://www.liquidweb.com/help-docs/"
					target="_blank"
					onClick={handleClose}
					role="menuitem"
				>
					<ListItemText primary="Help Docs" />
					<ListItemIcon>
						<ArrowUpRightIcon />
					</ListItemIcon>
				</MenuItem>
				<MenuItem
					component={HookedLink}
					href="/migration-center"
					onClick={handleClose}
					role="menuitem"
				>
					<ListItemText primary="Migration center" />
				</MenuItem>
				<Divider sx={{ marginX: -50 }} />
				<MenuItem
					onClick={() => {
						handleClose();
						setIsSecureNotesOpen(true);
					}}
					role="menuitem"
				>
					<ListItemText primary="Secure notes" />
					{hasSupportCases && <Badge color="error" variant="dot" />}
				</MenuItem>
				<Divider sx={{ marginX: -50 }} />
				<Stack sx={{ gap: 100, width: '100%', padding: 100 }}>
					<Box
						sx={{
							display: 'flex',
							alignItems: 'center',
							justifyContent: 'space-between',
							gap: 50,
							width: '100%',
						}}
					>
						<Typography
							variant="label-lg"
							sx={{
								color: ({ vars }) => vars.palette.uc.text.medium,
							}}
						>
							Support passphrase
						</Typography>
						<Stack sx={{ flexDirection: 'row', gap: 100 }}>
							<IconButton
								variant="barebones"
								size="small"
								role="menuitem"
								onClick={() => setShowSupportPassphrase((prev) => !prev)}
							>
								<EyeIcon show={showSupportPassphrase} />
							</IconButton>
							<IconButton
								variant="barebones"
								size="small"
								onClick={() => {
									handleClose();
									setIsEditUserOpen(true);
								}}
							>
								<PencilIcon />
							</IconButton>
						</Stack>
					</Box>

					<Typography
						variant="label-lg"
						sx={{ textWrap: 'wrap', fontWeight: 500 }}
					>
						{isLoadingSupportPassphrase ? (
							<Skeleton variant="text" sx={{ width: '203px' }} />
						) : (
							supportPassphrase
						)}
					</Typography>
				</Stack>
			</Menu>
		</>
	);
}

export default TopNavSupport;
